import useFetch from '@hooks/useFetch';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMeUid } from '@api/selectors/auth';
import { GroupsStatus } from 'types/groups';

const sortMembersAccordingToStatus = (users, groupsStatus) => {
  const statusMap = groupsStatus.reduce(
    (acc, { userUid: uid, status }) => ({
      ...acc,
      [uid]: status
    }),
    {}
  );

  const updatedUsers = users.map((user) => ({
    ...user,
    status: statusMap[user.uid]
  }));
  const onlineUsers = updatedUsers.filter(
    (user) => user.status === GroupsStatus.ONLINE
  );
  const offlineUsers = updatedUsers.filter(
    (user) => user.status === GroupsStatus.OFFLINE
  );

  return [...onlineUsers, ...offlineUsers];
};

const appendPositionToScoreboard = (scoreboard) => {
  if (!scoreboard) return null;

  return scoreboard.map((user, i) => ({
    ...user,
    position: i + 1
  }));
};

const modifyMembersData = (groupsDetails, groupsStatus, meUid) => {
  const {
    users = [],
    competitionEndTimestamp,
    competitionStartTimestamp,
    currentEpoch,
    leaderboardData,
    scoreboardData,
    ...rest
  } = groupsDetails;

  const scoreboard =
    appendPositionToScoreboard(scoreboardData) ||
    appendPositionToScoreboard(leaderboardData) ||
    [];

  const meUser = users.find((x) => x.uid === meUid);
  const meUserRank = scoreboard.findIndex((x) => x.uid === meUid);

  let usersList = users;
  if (meUser) {
    usersList = [meUser, ...usersList.filter((x) => x.uid !== meUid)];
  }

  const sortedMembersList = sortMembersAccordingToStatus(
    usersList,
    groupsStatus
  );

  return {
    leaderboardData: appendPositionToScoreboard(leaderboardData),
    scoreboardData: scoreboard,
    isGroupsDataLoaded: true,
    users: sortedMembersList,
    startDate: new Date(competitionStartTimestamp),
    endDate: new Date(competitionEndTimestamp),
    currentDate: new Date(currentEpoch),
    meUserRank: meUserRank + 1,
    ...rest
  };
};

const useGroupMembersDetails = (goalUID, groupUID) => {
  const meUid = useMyInfo(getMeUid);
  const groupsDetailsObj = useFetch(
    groupUID ? `v1/group/detail/?group_uid=${groupUID}` : null,
    {},
    {}
  );
  const groupsStatusObj = useFetch(
    groupUID
      ? `v1/group/member/status/?goal_uid=${goalUID}&group_uid=${groupUID}`
      : null,
    {},
    {}
  );

  if (!groupUID)
    return {
      isGroupsDataLoaded: true
    };

  const {
    data: groupsDetails,
    isLoading: isDetailsLoading,
    isError: detailsError
  } = groupsDetailsObj;

  const {
    data: groupsStatus,
    isLoading: isStatusLoading,
    isError: statusError
  } = groupsStatusObj;

  if (isDetailsLoading || isStatusLoading || detailsError || statusError)
    return {
      isGroupsDataLoaded: false
    };

  const data = modifyMembersData(groupsDetails, groupsStatus, meUid);
  return data;
};

export default useGroupMembersDetails;
